import {
  Body,
  Box,
  Flex,
  Heading,
  Icon,
  VStack,
  useToast,
} from '@hover/blueprint';
import { iCheckCircle } from '@hover/icons';
import React, { useEffect } from 'react';

import type { LeadFormData } from 'src/modules/pro/components/LeadForm';
import { LeadForm } from 'src/modules/pro/components/LeadForm';
import { useProPageContext } from 'src/modules/pro/hooks/useProPageContext';
import { useAuthenticationInfoQuery } from 'src/shared/graphql/hooks';
import { StatusError } from 'src/shared/helpers/request';
import { useElementVisibility } from 'src/shared/hooks';
import { createLead } from 'src/shared/requests/createLead';
import { buttonPressed } from 'src/shared/telemetry/generated/segment';
import { logErrorWithTags } from 'src/shared/telemetry/logger';

import { useProTranslate } from '../hooks';
import { ProPageSheetSteps } from '../providers/ProPageProvider';

const LeadFormSection = () => {
  const t = useProTranslate();
  const toast = useToast();
  const [visiblityRef, isLeadFormVisible] = useElementVisibility();
  const {
    leadData,
    dispatchLeadResponse,
    leadFormRef,
    orgDisplayName,
    leadSubmitted,
    publicIdentifier,
    setProPageSheetStep,
    genericTrackingData,
    setIsLeadFormVisible,
  } = useProPageContext();
  const [queryAuthenticationInfo] = useAuthenticationInfoQuery();

  useEffect(() => {
    setIsLeadFormVisible(isLeadFormVisible);
  }, [isLeadFormVisible, setIsLeadFormVisible]);

  const onSubmit = async ({
    fullName,
    postalCode,
    phoneNumber,
    email,
  }: LeadFormData) => {
    buttonPressed({
      ...genericTrackingData,
      button_text: 'Submit',
      button_location: 'bottom form',
      track_id: 'get-in-touch-form-submit',
    });
    try {
      const leadResponse = await createLead(
        publicIdentifier,
        email,
        phoneNumber,
        postalCode,
        fullName,
      );

      dispatchLeadResponse(leadResponse);
      const {
        data: {
          authenticationInfo: { emailInUse },
        },
      } = await queryAuthenticationInfo({ email });

      if (emailInUse) {
        setProPageSheetStep(ProPageSheetSteps.FOUND_ACCOUNT);
      } else {
        setProPageSheetStep(ProPageSheetSteps.CREATE_ACCOUNT);
      }
    } catch (error) {
      const errorStatusCode =
        error instanceof StatusError ? error.response.status : 500;

      if (errorStatusCode !== 429) {
        logErrorWithTags(error as Error, {
          featureName: 'LeadLandingPage',
        });
      }

      toast({
        description: t('leadForm.theresAnIssue'),
        props: { colorScheme: 'dark' },
        status: 'error',
      });
    }
  };

  const SuccessMessage = () => (
    <Flex
      flexDirection="column"
      justifyContent="center"
      width={{ base: 1, tablet: 750 }}
    >
      <Body
        alignItems="center"
        display="flex"
        flexDirection="column"
        isSemiBold
        size={{ base: 400, desktop: 500 }}
      >
        <Icon color="neutral.700" icon={iCheckCircle} size="huge" />
        {t('leadForm.gotIt')}
      </Body>
      <Heading size="xl" textAlign="center">
        {t('leadForm.weWillReachOutSoon', {
          firstName: leadData.firstName,
        })}
      </Heading>
    </Flex>
  );

  return (
    <Box
      height={leadSubmitted ? 850 : 1}
      paddingX={{ base: 500, tablet: 1200 }}
      paddingY={{ base: 1000, tablet: 1200 }}
      ref={leadFormRef}
    >
      {leadSubmitted ? (
        <SuccessMessage />
      ) : (
        <Flex
          flexDirection={{ base: 'column', tablet: 'row' }}
          justifyContent={{ base: 'center', tablet: 'space-between' }}
          w={1}
        >
          <Box marginBottom={500} w={1}>
            <VStack alignItems="flex-start" w={{ base: 1, desktop: 0.6 }}>
              <Heading
                marginBottom={{ base: 0, tablet: 400 }}
                size={{ base: 'large', tablet: 'xl' }}
                textAlign="left"
              >
                {t('leadForm.getInTouchWith', {
                  orgDisplayName,
                })}
              </Heading>
              <Body color="neutral.550" size="small" textAlign="left">
                {t('leadForm.aRepresentativeWillReachOutSoon')}
              </Body>
            </VStack>
          </Box>
          <Box
            alignItems={{ base: 'center', tablet: 'flex-end' }}
            width={{ base: 1, desktop: 2 / 3 }}
          >
            <LeadForm
              onSubmit={onSubmit}
              trackingData={{
                ...genericTrackingData,
                button_location: 'bottom form',
              }}
            />
            <Box ref={visiblityRef} />
          </Box>
        </Flex>
      )}
    </Box>
  );
};

export { LeadFormSection };
